import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import {
  addOneToOneComment,
  completeOneToOne,
  getMyOneToOnes,
  getOneToOne,
  getTeamOneToOnes,
  saveOneToOne,
} from "./appraisalApi";
import {
  AppraisalGuideButton,
  AppraisalHelpModal,
  AppraisalSectionHeader,
} from "./AppraisalHelp";
import { AppraisalEmployeeCell, pickEmployeeFields } from "./AppraisalEmployeeCell";
import { APPRAISAL_LIMITS, clampLen, validateFields } from "./appraisalValidation";
import { AppraisalCharCount } from "./AppraisalFormFields";
import "./Appraisal.css";

const emptyForm = () => ({
  id: "",
  headline: "",
  notesAndActions: "",
  startTime: "",
  finishTime: "",
  isCompleted: false,
});

const normalizeOneToOne = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const employee = pickEmployeeFields(raw);
  return {
    ...raw,
    id: raw.id ?? raw.Id,
    userId: raw.userId ?? raw.UserId ?? "",
    ...employee,
    managerId: raw.managerId ?? raw.ManagerId ?? "",
    headline: raw.headline ?? raw.Headline ?? "",
    notesAndActions: raw.notesAndActions ?? raw.NotesAndActions ?? "",
    startTime: raw.startTime ?? raw.StartTime ?? "",
    finishTime: raw.finishTime ?? raw.FinishTime ?? "",
    isCompleted: Boolean(raw.isCompleted ?? raw.IsCompleted),
    createdDate: raw.createdDate ?? raw.CreatedDate,
    comments: raw.comments ?? raw.Comments ?? [],
  };
};

const normalizeComment = (c) => ({
  id: c.id ?? c.Id,
  userId: c.userId ?? c.UserId ?? "",
  userName: c.userName ?? c.UserName ?? "",
  commentText: c.commentText ?? c.CommentText ?? "",
  dateAdded: c.dateAdded ?? c.DateAdded,
});

/** mode: "mine" | "team" | "detail" */
const AppraisalOneToOnePage = ({ mode = "mine" }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [rows, setRows] = useState([]);
  const [item, setItem] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showForm, setShowForm] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [helpKey, setHelpKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const [teamDetailId, setTeamDetailId] = useState(null);

  const isTeam = mode === "team";
  const detailId = isTeam ? teamDetailId : id || null;
  const showingDetail = Boolean(detailId) || mode === "detail";
  const fromTeam =
    isTeam ||
    location.state?.from === "team" ||
    new URLSearchParams(location.search || "").get("from") === "team";

  const title = useMemo(() => {
    if (isTeam) return t("app.appraisal.teamOneToOneTitle", { defaultValue: "Team One-to-ones" });
    if (showingDetail) return t("app.appraisal.oneToOneDetailTitle", { defaultValue: "One-to-one detail" });
    return t("app.appraisal.myOneToOneTitle", { defaultValue: "My One-to-ones" });
  }, [showingDetail, isTeam, t]);

  const openCount = useMemo(
    () => rows.filter((r) => !r.isCompleted).length,
    [rows]
  );

  const load = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      setError("");
      try {
        if (detailId) {
          const detail = normalizeOneToOne(await getOneToOne(detailId));
          setItem(detail);
          setForm({
            id: detail.id,
            headline: detail.headline || "",
            notesAndActions: detail.notesAndActions || "",
            startTime: detail.startTime || "",
            finishTime: detail.finishTime || "",
            isCompleted: !!detail.isCompleted,
          });
        } else {
          const list = isTeam ? await getTeamOneToOnes() : await getMyOneToOnes();
          setRows((Array.isArray(list) ? list : []).map(normalizeOneToOne).filter(Boolean));
        }
      } catch (e) {
        setError(e?.message || "Failed to load one-to-ones");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [detailId, isTeam]
  );

  useEffect(() => {
    load();
  }, [load]);

  const openNewForm = () => {
    setForm(emptyForm());
    setShowForm(true);
    setError("");
    setMessage("");
  };

  const openItem = (itemId) => {
    if (isTeam) {
      setTeamDetailId(itemId);
      setError("");
      setMessage("");
      return;
    }
    history.push(`/appraisal/one-to-one/${itemId}`);
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage("");
    setError("");
    const validationError = validateFields([
      { value: form.headline, required: true, max: APPRAISAL_LIMITS.headline, label: "Meeting topic" },
      { value: form.notesAndActions, max: APPRAISAL_LIMITS.longText, label: "Notes and actions" },
      { value: form.startTime, max: APPRAISAL_LIMITS.timeText, label: "Start time" },
      { value: form.finishTime, max: APPRAISAL_LIMITS.timeText, label: "Finish time" },
    ]);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    try {
      const saved = await saveOneToOne(form);
      setMessage(
        t("app.appraisal.oneToOne.saved", { defaultValue: "One-to-one saved." })
      );
      setShowForm(false);
      if (saved?.id && !showingDetail) {
        history.push(`/appraisal/one-to-one/${saved.id}`);
      } else {
        await load({ silent: true });
      }
    } catch (err) {
      setError(err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const onComplete = async () => {
    if (completing || !item?.id) return;
    setCompleting(true);
    setError("");
    try {
      await completeOneToOne({ oneToOneId: item.id });
      setMessage(
        t("app.appraisal.oneToOne.completed", {
          defaultValue: "Marked complete.",
        })
      );
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to complete");
    } finally {
      setCompleting(false);
    }
  };

  const onComment = async () => {
    if (!commentText.trim() || postingComment || !item?.id) return;
    setPostingComment(true);
    setError("");
    try {
      await addOneToOneComment({ oneToOneId: item.id, commentText });
      setCommentText("");
      setMessage(
        t("app.appraisal.oneToOne.commentAdded", {
          defaultValue: "Comment added.",
        })
      );
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to add comment");
    } finally {
      setPostingComment(false);
    }
  };

  const comments = (item?.comments || []).map(normalizeComment);

  return (
    <div className={`appraisal-page${!isTeam && !showingDetail ? " appraisal-my-oto-page" : ""}`}>
      <CommonPageHeader
        icon={<i className="bi bi-people" />}
        title={title}
        description={
          showingDetail
            ? item?.headline || ""
            : isTeam
              ? t("app.appraisal.teamOneToOneSubtitle", {
                  defaultValue:
                    "Review one-to-ones with your team — open a session to read notes or follow up.",
                })
              : t("app.appraisal.oneToOneSubtitle", {
                  defaultValue:
                    "Schedule check-ins with your manager, capture notes, and track follow-up actions.",
                })
        }
        rightContent={
          !showingDetail ? (
            <div className="appraisal-page-header-actions">
              <AppraisalGuideButton onClick={() => setHelpKey("oneToOne")} />
              {!isTeam ? (
                <button
                  type="button"
                  className="appraisal-btn primary appraisal-header-cta"
                  onClick={openNewForm}
                >
                  <i className="bi bi-plus-lg" aria-hidden />
                  {t("app.appraisal.oneToOne.new", {
                    defaultValue: "New one-to-one",
                  })}
                </button>
              ) : null}
            </div>
          ) : null
        }
      />
      {loading ? <div className="appraisal-muted">Loading…</div> : null}
      {error ? <div className="appraisal-error">{error}</div> : null}
      {message ? <div className="appraisal-success">{message}</div> : null}

      {!showingDetail && !isTeam ? (
        <section className="appraisal-settings-panel appraisal-oto-panel">
          <div className="appraisal-settings-panel__head appraisal-goals-panel__head">
            <div>
              <AppraisalSectionHeader
                icon="bi-people"
                title={t("app.appraisal.oneToOne.helpCardTitle", {
                  defaultValue: "My one-to-ones",
                })}
                helpKey="oneToOne"
                onOpenHelp={setHelpKey}
                tone="oneToOne"
              />
              <p className="appraisal-framework-hint">
                {t("app.appraisal.oneToOne.hint", {
                  defaultValue:
                    "Ongoing manager–employee check-ins outside the formal appraisal cycle.",
                })}
              </p>
            </div>
            <div className="appraisal-goals-panel__stats">
              <div className="appraisal-goals-stat">
                <strong>{rows.length}</strong>
                <span>{t("app.appraisal.oneToOne.totalLabel", { defaultValue: "Total" })}</span>
              </div>
              <div className="appraisal-goals-stat">
                <strong>{openCount}</strong>
                <span>{t("app.appraisal.oneToOne.openLabel", { defaultValue: "Open" })}</span>
              </div>
            </div>
          </div>

          {showForm ? (
            <form onSubmit={onSave} className="appraisal-framework-form appraisal-oto-create">
              <div className="appraisal-pdp-create__intro">
                <div className="appraisal-pdp-create__icon" aria-hidden>
                  <i className="bi bi-calendar-plus" />
                </div>
                <div>
                  <strong>
                    {t("app.appraisal.oneToOne.createTitle", {
                      defaultValue: "Schedule a one-to-one",
                    })}
                  </strong>
                  <p>
                    {t("app.appraisal.oneToOne.createHint", {
                      defaultValue:
                        "Add a clear topic and optional times. After the meeting, update notes and mark it complete.",
                    })}
                  </p>
                </div>
              </div>

              <div className="appraisal-pdp-steps" aria-hidden>
                <span className="appraisal-pdp-steps__item is-current">
                  <em>1</em>
                  {t("app.appraisal.oneToOne.stepSchedule", { defaultValue: "Schedule" })}
                </span>
                <i className="bi bi-chevron-right" />
                <span className="appraisal-pdp-steps__item">
                  <em>2</em>
                  {t("app.appraisal.oneToOne.stepNotes", { defaultValue: "Notes" })}
                </span>
                <i className="bi bi-chevron-right" />
                <span className="appraisal-pdp-steps__item">
                  <em>3</em>
                  {t("app.appraisal.oneToOne.stepComplete", { defaultValue: "Complete" })}
                </span>
              </div>

              <label className="appraisal-field">
                {t("app.appraisal.oneToOne.headline", { defaultValue: "Meeting topic" })}
                <input
                  required
                  disabled={saving}
                  maxLength={APPRAISAL_LIMITS.headline}
                  placeholder={t("app.appraisal.oneToOne.headlinePlaceholder", {
                    defaultValue: "e.g. Mid-month progress check-in",
                  })}
                  value={form.headline}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      headline: clampLen(e.target.value, APPRAISAL_LIMITS.headline),
                    }))
                  }
                />
                <small className="appraisal-field-hint">
                  {t("app.appraisal.oneToOne.headlineHint", {
                    defaultValue: "A short title that makes this meeting easy to find later.",
                  })}
                </small>
                <AppraisalCharCount value={form.headline} max={APPRAISAL_LIMITS.headline} />
              </label>

              <label className="appraisal-field">
                {t("app.appraisal.oneToOne.notes", {
                  defaultValue: "Notes and actions",
                })}
                <textarea
                  rows={4}
                  disabled={saving}
                  maxLength={APPRAISAL_LIMITS.longText}
                  placeholder={t("app.appraisal.oneToOne.notesPlaceholder", {
                    defaultValue:
                      "Agenda before the meeting, or decisions and follow-ups after…",
                  })}
                  value={form.notesAndActions}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      notesAndActions: clampLen(e.target.value, APPRAISAL_LIMITS.longText),
                    }))
                  }
                />
                <AppraisalCharCount
                  value={form.notesAndActions}
                  max={APPRAISAL_LIMITS.longText}
                />
              </label>

              <div className="appraisal-grid-2">
                <label className="appraisal-field">
                  {t("app.appraisal.oneToOne.start", { defaultValue: "Start time" })}
                  <input
                    disabled={saving}
                    maxLength={APPRAISAL_LIMITS.timeText}
                    placeholder="09:00"
                    value={form.startTime}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        startTime: clampLen(e.target.value, APPRAISAL_LIMITS.timeText),
                      }))
                    }
                  />
                </label>
                <label className="appraisal-field">
                  {t("app.appraisal.oneToOne.finish", { defaultValue: "Finish time" })}
                  <input
                    disabled={saving}
                    maxLength={APPRAISAL_LIMITS.timeText}
                    placeholder="09:30"
                    value={form.finishTime}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        finishTime: clampLen(e.target.value, APPRAISAL_LIMITS.timeText),
                      }))
                    }
                  />
                </label>
              </div>

              <div className="appraisal-framework-form__actions">
                <button
                  type="button"
                  className="appraisal-btn"
                  disabled={saving}
                  onClick={() => {
                    setShowForm(false);
                    setForm(emptyForm());
                  }}
                >
                  {t("app.common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button type="submit" className="appraisal-btn primary" disabled={saving}>
                  {saving
                    ? t("app.common.saving", { defaultValue: "Saving…" })
                    : t("app.appraisal.oneToOne.save", {
                        defaultValue: "Save one-to-one",
                      })}
                </button>
              </div>
            </form>
          ) : null}

          <div className="appraisal-framework-list-block">
            <div className="appraisal-framework-list-block__head">
              <span>
                {t("app.appraisal.oneToOne.listTitle", {
                  defaultValue: "Your one-to-ones",
                })}
              </span>
              <span className="appraisal-framework-count">{rows.length}</span>
            </div>

            {!loading && rows.length === 0 ? (
              <div className="appraisal-framework-empty">
                <i className="bi bi-people" aria-hidden />
                <p>
                  {t("app.appraisal.noOneToOnes", {
                    defaultValue:
                      "No one-to-ones yet. Schedule your first check-in with your manager.",
                  })}
                </p>
                {!showForm ? (
                  <button
                    type="button"
                    className="appraisal-btn primary"
                    onClick={openNewForm}
                  >
                    <i className="bi bi-plus-lg" aria-hidden />
                    {t("app.appraisal.oneToOne.new", {
                      defaultValue: "New one-to-one",
                    })}
                  </button>
                ) : null}
              </div>
            ) : null}

            {!loading && rows.length > 0 ? (
              <div className="appraisal-table-wrap appraisal-team-table-wrap appraisal-goals-table-wrap">
                <table className="appraisal-table appraisal-team-table">
                  <thead>
                    <tr>
                      <th>{t("app.appraisal.colHeadline", { defaultValue: "Topic" })}</th>
                      <th>{t("app.appraisal.colDate", { defaultValue: "Date" })}</th>
                      <th>{t("app.appraisal.colStatus", { defaultValue: "Status" })}</th>
                      <th className="appraisal-team-col-actions">
                        {t("app.appraisal.colActions", { defaultValue: "Actions" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <div className="appraisal-goal-headline">{r.headline || "—"}</div>
                          {r.startTime || r.finishTime ? (
                            <div className="appraisal-goal-objective-meta">
                              {r.startTime || "?"} – {r.finishTime || "?"}
                            </div>
                          ) : null}
                        </td>
                        <td className="appraisal-team-col-date">
                          {r.createdDate
                            ? new Date(r.createdDate).toLocaleDateString()
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`appraisal-status-pill ${
                              r.isCompleted
                                ? "appraisal-status-pill--oto-done"
                                : "appraisal-status-pill--oto-open"
                            }`}
                          >
                            {r.isCompleted
                              ? t("app.appraisal.statusCompleted", {
                                  defaultValue: "Completed",
                                })
                              : t("app.appraisal.statusOpen", {
                                  defaultValue: "Open",
                                })}
                          </span>
                        </td>
                        <td className="appraisal-team-col-actions">
                          <button
                            type="button"
                            className="appraisal-btn primary appraisal-team-action"
                            onClick={() => openItem(r.id)}
                          >
                            <i className="bi bi-box-arrow-up-right" aria-hidden />
                            {t("app.appraisal.open", { defaultValue: "Open" })}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {!showingDetail && isTeam ? (
        <>
          <section className="appraisal-card">
            <AppraisalSectionHeader
              icon="bi-people"
              title={t("app.appraisal.oneToOne.helpCardTitle", {
                defaultValue: "Team one-to-ones",
              })}
              helpKey="oneToOne"
              onOpenHelp={setHelpKey}
              tone="oneToOne"
            />
            <p className="appraisal-framework-hint" style={{ marginBottom: 0 }}>
              {t("app.appraisal.oneToOne.teamHint", {
                defaultValue:
                  "Review check-ins across your team — open a session to read notes or follow up.",
              })}
            </p>
          </section>

          {!loading && rows.length === 0 ? (
            <div className="appraisal-team-empty">
              <i className="bi bi-people" aria-hidden />
              <p>
                {t("app.appraisal.noTeamOneToOnes", {
                  defaultValue: "No team one-to-ones yet.",
                })}
              </p>
            </div>
          ) : null}

          {!loading && rows.length > 0 ? (
            <div className="appraisal-table-wrap appraisal-team-table-wrap">
              <table className="appraisal-table appraisal-team-table">
                <thead>
                  <tr>
                    <th className="appraisal-team-col-employee">
                      {t("app.appraisal.colEmployee", { defaultValue: "Employee" })}
                    </th>
                    <th>{t("app.appraisal.colHeadline", { defaultValue: "Topic" })}</th>
                    <th>{t("app.appraisal.colDate", { defaultValue: "Date" })}</th>
                    <th>{t("app.appraisal.colStatus", { defaultValue: "Status" })}</th>
                    <th className="appraisal-team-col-actions">
                      {t("app.appraisal.colActions", { defaultValue: "Actions" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="appraisal-team-col-employee">
                        <AppraisalEmployeeCell
                          name={r.userName}
                          designation={r.designation}
                          department={r.department}
                          officeName={r.officeName}
                          profileImageUrl={r.profileImageUrl}
                        />
                      </td>
                      <td>
                        <div className="appraisal-goal-headline">{r.headline || "—"}</div>
                      </td>
                      <td className="appraisal-team-col-date">
                        {r.createdDate
                          ? new Date(r.createdDate).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        <span
                          className={`appraisal-status-pill ${
                            r.isCompleted
                              ? "appraisal-status-pill--oto-done"
                              : "appraisal-status-pill--oto-open"
                          }`}
                        >
                          {r.isCompleted
                            ? t("app.appraisal.statusCompleted", {
                                defaultValue: "Completed",
                              })
                            : t("app.appraisal.statusOpen", {
                                defaultValue: "Open",
                              })}
                        </span>
                      </td>
                      <td className="appraisal-team-col-actions">
                        <button
                          type="button"
                          className="appraisal-btn primary appraisal-team-action"
                          onClick={() => openItem(r.id)}
                        >
                          <i className="bi bi-box-arrow-up-right" aria-hidden />
                          {t("app.appraisal.open", { defaultValue: "Open" })}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}

      {showingDetail && item ? (
        <>
          <div className="appraisal-card appraisal-checkin-detail">
            {isTeam ? (
              <button
                type="button"
                className="appraisal-checkin-back"
                onClick={() => {
                  setTeamDetailId(null);
                  setItem(null);
                  setCommentText("");
                }}
              >
                <i className="bi bi-arrow-left" aria-hidden />
                {t("app.appraisal.back", { defaultValue: "Back" })}
              </button>
            ) : (
              <Link
                className="appraisal-checkin-back"
                to={fromTeam ? "/appraisal/team-one-to-one" : "/appraisal/one-to-one"}
              >
                <i className="bi bi-arrow-left" aria-hidden />
                {t("app.appraisal.back", { defaultValue: "Back" })}
              </Link>
            )}
            <div className="appraisal-checkin-detail__head">
              <div>
                <h3>{item.headline || t("app.appraisal.oneToOne.untitled", { defaultValue: "One-to-one" })}</h3>
                <p className="appraisal-checkin-detail__meta">
                  {item.userName ? <span>{item.userName}</span> : null}
                  {item.designation ? <span>{item.designation}</span> : null}
                  {item.createdDate ? (
                    <span>{new Date(item.createdDate).toLocaleDateString()}</span>
                  ) : null}
                  {item.startTime || item.finishTime ? (
                    <span>
                      {item.startTime || "?"} – {item.finishTime || "?"}
                    </span>
                  ) : null}
                </p>
              </div>
              <span
                className={`appraisal-status-pill ${
                  item.isCompleted
                    ? "appraisal-status-pill--oto-done"
                    : "appraisal-status-pill--oto-open"
                }`}
              >
                {item.isCompleted
                  ? t("app.appraisal.statusCompleted", { defaultValue: "Completed" })
                  : t("app.appraisal.statusOpen", { defaultValue: "Open" })}
              </span>
            </div>
            {item.notesAndActions ? (
              <div className="appraisal-checkin-notes">
                <strong>
                  {t("app.appraisal.oneToOne.notes", {
                    defaultValue: "Notes and actions",
                  })}
                </strong>
                <p>{item.notesAndActions}</p>
              </div>
            ) : (
              <p className="appraisal-muted" style={{ marginBottom: 0 }}>
                {t("app.appraisal.oneToOne.noNotes", {
                  defaultValue: "No notes recorded yet.",
                })}
              </p>
            )}
          </div>

          {!item.isCompleted ? (
            <form className="appraisal-card" onSubmit={onSave}>
              <h4>
                {t("app.appraisal.oneToOne.editTitle", {
                  defaultValue: "Update this meeting",
                })}
              </h4>
              <p className="appraisal-framework-hint">
                {t("app.appraisal.oneToOne.editHint", {
                  defaultValue:
                    "Refine the topic and capture decisions or follow-ups, then mark complete when finished.",
                })}
              </p>
              <label className="appraisal-field">
                {t("app.appraisal.oneToOne.headline", { defaultValue: "Meeting topic" })}
                <input
                  required
                  disabled={saving}
                  maxLength={APPRAISAL_LIMITS.headline}
                  value={form.headline}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      headline: clampLen(e.target.value, APPRAISAL_LIMITS.headline),
                    }))
                  }
                />
                <AppraisalCharCount value={form.headline} max={APPRAISAL_LIMITS.headline} />
              </label>
              <label className="appraisal-field">
                {t("app.appraisal.oneToOne.notes", {
                  defaultValue: "Notes and actions",
                })}
                <textarea
                  rows={4}
                  disabled={saving}
                  maxLength={APPRAISAL_LIMITS.longText}
                  value={form.notesAndActions}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      notesAndActions: clampLen(e.target.value, APPRAISAL_LIMITS.longText),
                    }))
                  }
                />
                <AppraisalCharCount
                  value={form.notesAndActions}
                  max={APPRAISAL_LIMITS.longText}
                />
              </label>
              <div className="appraisal-grid-2">
                <label className="appraisal-field">
                  {t("app.appraisal.oneToOne.start", { defaultValue: "Start time" })}
                  <input
                    disabled={saving}
                    maxLength={APPRAISAL_LIMITS.timeText}
                    placeholder="09:00"
                    value={form.startTime}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        startTime: clampLen(e.target.value, APPRAISAL_LIMITS.timeText),
                      }))
                    }
                  />
                </label>
                <label className="appraisal-field">
                  {t("app.appraisal.oneToOne.finish", { defaultValue: "Finish time" })}
                  <input
                    disabled={saving}
                    maxLength={APPRAISAL_LIMITS.timeText}
                    placeholder="09:30"
                    value={form.finishTime}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        finishTime: clampLen(e.target.value, APPRAISAL_LIMITS.timeText),
                      }))
                    }
                  />
                </label>
              </div>
              <div className="appraisal-framework-form__actions">
                <button
                  type="button"
                  className="appraisal-btn"
                  disabled={completing || saving}
                  onClick={onComplete}
                >
                  {completing
                    ? t("app.common.saving", { defaultValue: "Saving…" })
                    : t("app.appraisal.markComplete", {
                        defaultValue: "Mark complete",
                      })}
                </button>
                <button type="submit" className="appraisal-btn primary" disabled={saving}>
                  {saving
                    ? t("app.common.saving", { defaultValue: "Saving…" })
                    : t("app.appraisal.saveChanges", {
                        defaultValue: "Save changes",
                      })}
                </button>
              </div>
            </form>
          ) : null}

          <section className="appraisal-card appraisal-comments-panel">
            <div className="appraisal-comments-panel__head">
              <span className="appraisal-comments-panel__icon" aria-hidden>
                <i className="bi bi-chat-left-text" />
              </span>
              <div>
                <h4>{t("app.appraisal.comments", { defaultValue: "Comments" })}</h4>
                <p>
                  {t("app.appraisal.oneToOne.commentsHint", {
                    defaultValue: "Discuss progress, blockers, or next steps.",
                  })}
                </p>
              </div>
            </div>
            <div className="appraisal-comments-composer">
              <label className="appraisal-field">
                {t("app.appraisal.addComment", { defaultValue: "Add comment" })}
                <textarea
                  rows={2}
                  disabled={postingComment}
                  maxLength={APPRAISAL_LIMITS.comment}
                  value={commentText}
                  onChange={(e) =>
                    setCommentText(clampLen(e.target.value, APPRAISAL_LIMITS.comment))
                  }
                  placeholder={t("app.appraisal.oneToOne.commentPlaceholder", {
                    defaultValue: "Follow up on an action, share context, or ask a question…",
                  })}
                />
                <AppraisalCharCount value={commentText} max={APPRAISAL_LIMITS.comment} />
              </label>
              <button
                type="button"
                className="appraisal-btn"
                disabled={!commentText.trim() || postingComment}
                onClick={onComment}
              >
                {postingComment
                  ? t("app.common.saving", { defaultValue: "Saving…" })
                  : t("app.appraisal.postComment", { defaultValue: "Post comment" })}
              </button>
            </div>
            {comments.length > 0 ? (
              <ul className="appraisal-checkin-comments">
                {comments.map((c) => (
                  <li key={c.id}>
                    <div className="appraisal-checkin-comments__head">
                      <strong>{c.userName || c.userId || "—"}</strong>
                      <span>
                        {c.dateAdded ? new Date(c.dateAdded).toLocaleString() : ""}
                      </span>
                    </div>
                    <p>{c.commentText}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="appraisal-muted" style={{ marginTop: "1rem", marginBottom: 0 }}>
                {t("app.appraisal.noComments", { defaultValue: "No comments yet." })}
              </p>
            )}
          </section>
        </>
      ) : null}

      <AppraisalHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AppraisalOneToOnePage;
